<template>
    <PanelFrame ref="panelFrame" panel-class="spotlight-app" resize-directions="all">
        <template #titlebar-left>
            <span class="spotlight-titlebar-icon">
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5"><circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/></svg>
            </span>
            <span class="spotlight-titlebar-text">{{ $t('common.spotlight.title') }}</span>
        </template>

        <template #titlebar-actions>
            <button class="panel-titlebar-btn" @click.stop="showGuide = true" :title="$t('common.spotlight.guide.title')">
                <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="10"/><path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3"/><line x1="12" y1="17" x2="12.01" y2="17"/></svg>
            </button>
        </template>

        <template #default>
            <div class="spotlight-body">
                <div class="spotlight-search-bar" :class="{ 'spotlight-search-bar--no-border': !hasAnyContent }">
                    <input ref="searchInput"
                           class="spotlight-search-input"
                           v-model="searchQuery"
                           :placeholder="$t('common.spotlight.placeholder')"
                           autocomplete="off"
                           spellcheck="false"
                           @keydown.down.prevent="focusNext"
                           @keydown.up.prevent="focusPrev"
                           @keydown.enter.prevent="activateResult" />
                    <button v-if="searchQuery" class="spotlight-search-clear" @click="clearSearch">
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
                    </button>
                    <svg v-else class="spotlight-search-hint-icon" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/></svg>
                </div>

                <div v-show="hasAnyContent" class="spotlight-results" ref="resultsContainer">
                    <div v-if="hasWidget" class="spotlight-widget-box">
                        <MathCalculator v-if="mathResult" :data="mathResult" />
                        <CurrencyMathCalculator v-else-if="currencyMathQuery" :query="currencyMathQuery" />
                        <ExchangeCalculator v-else-if="exchangeQuery" :query="exchangeQuery" />
                        <UnitConverter v-else-if="unitQuery" :query="unitQuery" />
                    </div>
                    <div v-if="hasWidget && (hasResults || hasQuickActions || weatherItems.length)" class="spotlight-widget-divider"></div>

                    <div v-if="weatherItems.length" class="spotlight-widget-box">
                        <WeatherWidget v-for="wi in weatherItems" :key="wi.id" :item="wi" />
                    </div>
                    <div v-if="weatherItems.length && (hasResults || hasQuickActions)" class="spotlight-widget-divider"></div>

                    <div v-if="hasQuickActions" class="spotlight-quick-actions">
                        <div class="spotlight-quick-actions-header">{{ $t('common.spotlight.quickActions.title') }}</div>
                        <div v-for="(qa, qaIdx) in quickActions" :key="qa.action"
                             class="spotlight-quick-action"
                             :class="{ 'spotlight-quick-action--focused': focusedQuickActionIdx === qaIdx }"
                             @click="executeQuickAction(qa)">
                            <div class="spotlight-result-icon" :class="'spotlight-result-icon--' + qa.icon">
                                <svg v-if="qa.icon === 'note'" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/><line x1="12" y1="18" x2="12" y2="12"/><line x1="9" y1="15" x2="15" y2="15"/></svg>
                                <svg v-else-if="qa.icon === 'agenda'" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><rect x="3" y="4" width="18" height="18" rx="2" ry="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/><line x1="12" y1="16" x2="12" y2="13"/><line x1="10.5" y1="14.5" x2="13.5" y2="14.5"/></svg>
                                <svg v-else-if="qa.icon === 'customer'" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/><circle cx="12" cy="7" r="4"/></svg>
                                <svg v-else-if="qa.icon === 'officer'" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M16 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="8.5" cy="7" r="4"/><line x1="20" y1="8" x2="20" y2="14"/><line x1="23" y1="11" x2="17" y2="11"/></svg>
                                <svg v-else width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M14.7 6.3a1 1 0 0 0 0 1.4l1.6 1.6a1 1 0 0 0 1.4 0l3.77-3.77a6 6 0 0 1-7.94 7.94l-6.91 6.91a2.12 2.12 0 0 1-3-3l6.91-6.91a6 6 0 0 1 7.94-7.94l-3.76 3.76z"/></svg>
                            </div>
                            <div class="spotlight-quick-action-text">
                                <span class="spotlight-quick-action-label">{{ getQuickActionLabel(qa) }}</span>
                                <span class="spotlight-quick-action-hint">{{ getQuickActionHint(qa) }}</span>
                            </div>
                            <svg class="spotlight-quick-action-arrow" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><polyline points="9 18 15 12 9 6"/></svg>
                        </div>
                    </div>
                    <div v-if="hasQuickActions && hasResults" class="spotlight-widget-divider"></div>

                    <div v-if="loading" class="spotlight-loading" :class="{ 'spotlight-loading--inline': hasWidget || hasQuickActions }">
                        <div class="spotlight-loading-spinner" :class="{ 'spotlight-loading-spinner--small': hasWidget }"></div>
                    </div>


                    <div v-else-if="results !== null && filteredResults.length === 0 && !hasWidget && !hasQuickActions && !weatherItems.length" class="spotlight-empty">
                        <svg class="spotlight-empty-icon" width="44" height="44" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.2"><circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/><line x1="8" y1="8" x2="14" y2="14" stroke-width="1.5"/></svg>
                        <p>{{ $t('common.spotlight.noResults') }}</p>
                    </div>

                    <SpotlightGroup
                        v-for="group in filteredResults"
                        :key="group.key"
                        :group="group"
                        :collapsed="!!collapsedGroups[group.key]"
                        :focused-index="focusedGroupKey === group.key ? focusedIndexInGroup : -1"
                        :focused-group="focusedGroupKey"
                        @toggle="toggleGroup(group.key)"
                        @select="onResultSelect"
                    />
                </div>
            </div>
        </template>
    </PanelFrame>

    <BaseModal :visible="showGuide" width="520px" max-width="calc(100vw - 32px)"
               max-height="calc(100vh - 64px)" content-class="spotlight-guide"
               @close="showGuide = false">
                <div class="spotlight-guide-header">
                    <h3>{{ $t('common.spotlight.guide.title') }}</h3>
                    <button class="spotlight-guide-close" @click="showGuide = false">
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
                    </button>
                </div>
                <div class="spotlight-guide-body">
                    <div class="spotlight-guide-section">
                        <div class="spotlight-guide-section-icon spotlight-guide-icon--search">
                            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/></svg>
                        </div>
                        <div class="spotlight-guide-section-content">
                            <h4>{{ $t('common.spotlight.guide.searchTitle') }}</h4>
                            <p>{{ $t('common.spotlight.guide.searchDesc') }}</p>
                            <div class="sg-example-list sg-example--blue">
                                <div class="sg-example-row">
                                    <span class="sg-example-input">{{ $t('common.spotlight.guide.exampleCustomer') }}</span>
                                    <span class="sg-example-result">{{ $t('common.spotlight.guide.prefixCustomer') }}</span>
                                </div>
                                <div class="sg-example-row">
                                    <span class="sg-example-input">{{ $t('common.spotlight.guide.exampleVehicle') }}</span>
                                    <span class="sg-example-result">{{ $t('common.spotlight.guide.prefixVehicle') }}</span>
                                </div>
                                <div class="sg-example-row">
                                    <span class="sg-example-input">{{ $t('common.spotlight.guide.exampleStock') }}</span>
                                    <span class="sg-example-result">{{ $t('common.spotlight.guide.prefixStock') }}</span>
                                </div>
                            </div>
                        </div>
                    </div>

                    <div class="spotlight-guide-section">
                        <div class="spotlight-guide-section-icon spotlight-guide-icon--prefix">
                            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><polyline points="4 17 10 11 4 5"/><line x1="12" y1="19" x2="20" y2="19"/></svg>
                        </div>
                        <div class="spotlight-guide-section-content">
                            <h4>{{ $t('common.spotlight.guide.prefixTitle') }}</h4>
                            <p>{{ $t('common.spotlight.guide.prefixDesc') }}</p>
                            <div class="spotlight-guide-prefix-grid">
                                <div class="sg-prefix-card sg-prefix--blue"><span class="sg-prefix-code">cu:</span><span class="sg-prefix-label">{{ $t('common.spotlight.guide.prefixCustomer') }}</span></div>
                                <div class="sg-prefix-card sg-prefix--green"><span class="sg-prefix-code">v:</span><span class="sg-prefix-label">{{ $t('common.spotlight.guide.prefixVehicle') }}</span></div>
                                <div class="sg-prefix-card sg-prefix--orange"><span class="sg-prefix-code">s:</span><span class="sg-prefix-label">{{ $t('common.spotlight.guide.prefixStock') }}</span></div>
                                <div class="sg-prefix-card sg-prefix--red"><span class="sg-prefix-code">vwo:</span><span class="sg-prefix-label">{{ $t('common.spotlight.guide.prefixWorkOrder') }}</span></div>
                                <div class="sg-prefix-card sg-prefix--yellow"><span class="sg-prefix-code">n:</span><span class="sg-prefix-label">{{ $t('common.spotlight.guide.prefixNote') }}</span></div>
                                <div class="sg-prefix-card sg-prefix--cyan"><span class="sg-prefix-code">a:</span><span class="sg-prefix-label">{{ $t('common.spotlight.guide.prefixAgenda') }}</span></div>
                                <div class="sg-prefix-card sg-prefix--orange"><span class="sg-prefix-code">h:</span><span class="sg-prefix-label">{{ $t('common.spotlight.guide.prefixWeather') }}</span></div>
                            </div>
                            <div class="sg-prefix-demo">
                                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5"><circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/></svg>
                                <span class="sg-prefix-demo-prefix">cu:</span><span class="sg-prefix-demo-term">{{ $t('common.spotlight.guide.prefixDemoTerm') }}</span>
                                <span class="sg-prefix-demo-arrow">→</span>
                                <span class="sg-prefix-demo-desc">{{ $t('common.spotlight.guide.prefixDemoDesc') }}</span>
                            </div>
                        </div>
                    </div>

                    <div class="spotlight-guide-section">
                        <div class="spotlight-guide-section-icon spotlight-guide-icon--math">
                            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><rect x="4" y="2" width="16" height="20" rx="2"/><line x1="8" y1="6" x2="16" y2="6"/><line x1="8" y1="10" x2="8.01" y2="10"/><line x1="12" y1="10" x2="12.01" y2="10"/><line x1="16" y1="10" x2="16.01" y2="10"/><line x1="8" y1="14" x2="8.01" y2="14"/><line x1="12" y1="14" x2="12.01" y2="14"/><line x1="16" y1="14" x2="16.01" y2="14"/><line x1="8" y1="18" x2="8.01" y2="18"/><line x1="12" y1="18" x2="16" y2="18"/></svg>
                        </div>
                        <div class="spotlight-guide-section-content">
                            <h4>{{ $t('common.spotlight.guide.mathTitle') }}</h4>
                            <p>{{ $t('common.spotlight.guide.mathDesc') }}</p>
                            <div class="sg-example-list sg-example--purple">
                                <div class="sg-example-row">
                                    <span class="sg-example-input">150 * 1.18</span>
                                    <span class="sg-example-result">= 177</span>
                                </div>
                                <div class="sg-example-row">
                                    <span class="sg-example-input">(500 - 120) / 3</span>
                                    <span class="sg-example-result">= 126,67</span>
                                </div>
                                <div class="sg-example-row">
                                    <span class="sg-example-input">2500 + 750</span>
                                    <span class="sg-example-result">= 3.250</span>
                                </div>
                                <div class="sg-example-row">
                                    <span class="sg-example-input">1000 + %18</span>
                                    <span class="sg-example-result">= 1.180</span>
                                </div>
                                <div class="sg-example-row">
                                    <span class="sg-example-input">5000 - %20</span>
                                    <span class="sg-example-result">= 4.000</span>
                                </div>
                            </div>
                        </div>
                    </div>

                    <div class="spotlight-guide-section">
                        <div class="spotlight-guide-section-icon spotlight-guide-icon--currency">
                            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"><line x1="12" y1="1" x2="12" y2="23"/><path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6"/></svg>
                        </div>
                        <div class="spotlight-guide-section-content">
                            <h4>{{ $t('common.spotlight.guide.currencyTitle') }}</h4>
                            <p>{{ $t('common.spotlight.guide.currencyDesc') }}</p>
                            <div class="sg-example-list sg-example--green">
                                <div class="sg-example-row">
                                    <span class="sg-example-input">42$</span>
                                    <span class="sg-example-result">USD → TRY</span>
                                </div>
                                <div class="sg-example-row">
                                    <span class="sg-example-input">100 euro</span>
                                    <span class="sg-example-result">EUR → TRY</span>
                                </div>
                                <div class="sg-example-row">
                                    <span class="sg-example-input">42 dolar kaç euro</span>
                                    <span class="sg-example-result">USD → EUR</span>
                                </div>
                                <div class="sg-example-row">
                                    <span class="sg-example-input">43$ * 1,2</span>
                                    <span class="sg-example-result">= 51,60 $</span>
                                </div>
                                <div class="sg-example-row">
                                    <span class="sg-example-input">43$ + 10€</span>
                                    <span class="sg-example-result">→ TRY</span>
                                </div>
                                <div class="sg-example-row">
                                    <span class="sg-example-input">2400$ + %18 kaç tl</span>
                                    <span class="sg-example-result">→ TRY</span>
                                </div>
                            </div>
                        </div>
                    </div>

                    <div class="spotlight-guide-section">
                        <div class="spotlight-guide-section-icon spotlight-guide-icon--unit">
                            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"><polyline points="17 1 21 5 17 9"/><path d="M3 11V9a4 4 0 0 1 4-4h14"/><polyline points="7 23 3 19 7 15"/><path d="M21 13v2a4 4 0 0 1-4 4H3"/></svg>
                        </div>
                        <div class="spotlight-guide-section-content">
                            <h4>{{ $t('common.spotlight.guide.unitTitle') }}</h4>
                            <p>{{ $t('common.spotlight.guide.unitDesc') }}</p>
                            <div class="sg-example-list sg-example--cyan">
                                <div class="sg-example-row">
                                    <span class="sg-example-input">{{ $t('common.spotlight.guide.unitEx1Input') }}</span>
                                    <span class="sg-example-result">{{ $t('common.spotlight.guide.unitEx1Result') }}</span>
                                </div>
                                <div class="sg-example-row">
                                    <span class="sg-example-input">{{ $t('common.spotlight.guide.unitEx2Input') }}</span>
                                    <span class="sg-example-result">{{ $t('common.spotlight.guide.unitEx2Result') }}</span>
                                </div>
                                <div class="sg-example-row">
                                    <span class="sg-example-input">{{ $t('common.spotlight.guide.unitEx3Input') }}</span>
                                    <span class="sg-example-result">{{ $t('common.spotlight.guide.unitEx3Result') }}</span>
                                </div>
                                <div class="sg-example-row">
                                    <span class="sg-example-input">{{ $t('common.spotlight.guide.unitEx4Input') }}</span>
                                    <span class="sg-example-result">{{ $t('common.spotlight.guide.unitEx4Result') }}</span>
                                </div>
                            </div>
                        </div>
                    </div>

                    <div class="spotlight-guide-section">
                        <div class="spotlight-guide-section-icon spotlight-guide-icon--weather">
                            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"><path d="M17.5 19H9a7 7 0 1 1 6.71-9h1.79a4.5 4.5 0 1 1 0 9z"/></svg>
                        </div>
                        <div class="spotlight-guide-section-content">
                            <h4>{{ $t('common.spotlight.guide.weatherTitle') }}</h4>
                            <p>{{ $t('common.spotlight.guide.weatherDesc') }}</p>
                            <div class="sg-example-list sg-example--orange">
                                <div class="sg-example-row">
                                    <span class="sg-example-input">{{ $t('common.spotlight.guide.weatherEx1Input') }}</span>
                                    <span class="sg-example-result">{{ $t('common.spotlight.guide.weatherEx1Result') }}</span>
                                </div>
                                <div class="sg-example-row">
                                    <span class="sg-example-input">{{ $t('common.spotlight.guide.weatherEx2Input') }}</span>
                                    <span class="sg-example-result">{{ $t('common.spotlight.guide.weatherEx2Result') }}</span>
                                </div>
                                <div class="sg-example-row">
                                    <span class="sg-example-input">{{ $t('common.spotlight.guide.weatherEx3Input') }}</span>
                                    <span class="sg-example-result">{{ $t('common.spotlight.guide.weatherEx3Result') }}</span>
                                </div>
                            </div>
                            <div class="spotlight-guide-prefix-grid" style="margin-top: 10px; grid-template-columns: 1fr 1fr;">
                                <div class="sg-prefix-card sg-prefix--orange"><span class="sg-prefix-code">hava:</span><span class="sg-prefix-label">{{ $t('common.spotlight.guide.weatherPrefixLabel') }}</span></div>
                                <div class="sg-prefix-card sg-prefix--orange"><span class="sg-prefix-code">h:</span><span class="sg-prefix-label">{{ $t('common.spotlight.guide.weatherPrefixShort') }}</span></div>
                            </div>
                        </div>
                    </div>

                    <div class="spotlight-guide-section">
                        <div class="spotlight-guide-section-icon spotlight-guide-icon--keyboard">
                            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><rect x="2" y="4" width="20" height="16" rx="2"/><line x1="6" y1="8" x2="6.01" y2="8"/><line x1="10" y1="8" x2="10.01" y2="8"/><line x1="14" y1="8" x2="14.01" y2="8"/><line x1="18" y1="8" x2="18.01" y2="8"/><line x1="6" y1="12" x2="6.01" y2="12"/><line x1="10" y1="12" x2="10.01" y2="12"/><line x1="14" y1="12" x2="14.01" y2="12"/><line x1="18" y1="12" x2="18.01" y2="12"/><line x1="8" y1="16" x2="16" y2="16"/></svg>
                        </div>
                        <div class="spotlight-guide-section-content">
                            <h4>{{ $t('common.spotlight.guide.shortcutsTitle') }}</h4>
                            <div class="spotlight-guide-shortcuts">
                                <div><kbd>{{ isMac ? '⌥ Option' : 'Alt' }}</kbd> + <kbd>S</kbd> <span>{{ $t('common.spotlight.guide.shortcutToggle') }}</span></div>
                                <div><kbd>↑</kbd> <kbd>↓</kbd> <span>{{ $t('common.spotlight.guide.shortcutNavigate') }}</span></div>
                                <div><kbd>Enter</kbd> <span>{{ $t('common.spotlight.guide.shortcutSelect') }}</span></div>
                                <div><kbd>Esc</kbd> <span>{{ $t('common.spotlight.guide.shortcutEsc') }}</span></div>
                            </div>
                        </div>
                    </div>
                </div>
    </BaseModal>
</template>

<script>
import { createPanelMixin } from '@/components/panel/usePanelBase'
import panelManager from '@/core/helpers/panelManager'
import PanelFrame from '@/components/panel/PanelFrame.vue'
import BaseModal from '@/components/common/base-modal/BaseModal.vue'
import SpotlightGroup from './SpotlightGroup.vue'
import MathCalculator from './widgets/math/MathCalculator.vue'
import ExchangeCalculator from './widgets/currency/ExchangeCalculator.vue'
import CurrencyMathCalculator from './widgets/currency/CurrencyMathCalculator.vue'
import UnitConverter from './widgets/unit/UnitConverter.vue'
import WeatherWidget from './widgets/weather/WeatherWidget.vue'
import { parseUnitQuery } from './widgets/unit/unitData'

function parseMathExpression(query) {
    if (!query || query.trim().length < 2) return null
    const trimmed = query.trim()
    if (!/^[\d\s+\-*/().,%]+$/.test(trimmed)) return null
    if (!/[+\-*/%]/.test(trimmed)) return null
    if (!/\d/.test(trimmed)) return null
    try {
        let expr = trimmed.replace(/,/g, '.').replace(/[^0-9+\-*/().%\s]/g, '')

        let m, pct = false
        if (!pct) { m = expr.match(/^(.+)\s*\+\s*%(\d+(?:\.\d+)?)\s*$/) || expr.match(/^(.+)\s*\+\s*(\d+(?:\.\d+)?)%\s*$/); if (m) { expr = '(' + m[1] + ')*(1+' + m[2] + '/100)'; pct = true } }
        if (!pct) { m = expr.match(/^(.+)\s*-\s*%(\d+(?:\.\d+)?)\s*$/) || expr.match(/^(.+)\s*-\s*(\d+(?:\.\d+)?)%\s*$/); if (m) { expr = '(' + m[1] + ')*(1-' + m[2] + '/100)'; pct = true } }
        if (!pct) { m = expr.match(/^(.+)\s*\*\s*%(\d+(?:\.\d+)?)\s*$/) || expr.match(/^(.+)\s*\*\s*(\d+(?:\.\d+)?)%\s*$/); if (m) { expr = '(' + m[1] + ')*(' + m[2] + '/100)'; pct = true } }
        if (!pct) { m = expr.match(/^(.+)\s*\/\s*%(\d+(?:\.\d+)?)\s*$/) || expr.match(/^(.+)\s*\/\s*(\d+(?:\.\d+)?)%\s*$/); if (m) { expr = '(' + m[1] + ')/(' + m[2] + '/100)'; pct = true } }
        if (!pct) { m = expr.match(/^(.+)\s*%\s*(\d+(?:\.\d+)?)\s*$/); if (m) { expr = '(' + m[1] + ')*(' + m[2] + '/100)'; pct = true } }
        if (!pct) { expr = expr.replace(/(\d+(?:\.\d+)?)%/g, '($1/100)'); expr = expr.replace(/%(\d+(?:\.\d+)?)/g, '($1/100)') }

        const fn = new Function('return (' + expr + ')')
        const result = fn()
        if (typeof result !== 'number' || !isFinite(result)) return null
        return { expression: trimmed, result }
    } catch (e) {
        return null
    }
}

const CURRENCY_ALIASES = {
    '$': 'USD', 'dolar': 'USD', 'usd': 'USD', 'dollar': 'USD',
    '€': 'EUR', 'euro': 'EUR', 'eur': 'EUR',
    '₺': 'TRY', 'tl': 'TRY', 'try': 'TRY',
    '£': 'GBP', 'sterlin': 'GBP', 'gbp': 'GBP',
    '₽': 'RUB', 'ruble': 'RUB', 'rub': 'RUB',
    '¥': 'JPY', 'yen': 'JPY', 'jpy': 'JPY',
    '₼': 'AZN', 'manat': 'AZN', 'azn': 'AZN',
    'chf': 'CHF', 'frank': 'CHF',
    'sar': 'SAR', 'riyal': 'SAR',
    'aed': 'AED', 'dirhem': 'AED',
    'yuan': 'CNY', 'cny': 'CNY',
    'kwd': 'KWD', 'dinar': 'KWD',
}

const SYMBOL_CHARS = '$€₺£₽¥₼'
const CURRENCY_WORDS = 'dolar|dollar|usd|euro|eur|tl|try|sterlin|gbp|ruble|rub|yen|jpy|manat|azn|chf|frank|sar|riyal|aed|dirhem|yuan|cny|kwd|dinar'
const TARGET_PATTERN = '(?:[' + SYMBOL_CHARS + ']|' + CURRENCY_WORDS + ')'

function parseExchangeQuery(query) {
    if (!query || query.trim().length < 2) return null
    const q = query.trim().toLowerCase()

    let match = q.match(new RegExp('^([' + SYMBOL_CHARS + '])\\s*([\\d.,]+)$'))
    if (match) {
        const from = CURRENCY_ALIASES[match[1]]
        const amount = parseFloat(match[2].replace(',', '.'))
        if (from && amount > 0) return { amount, from, to: null }
    }

    match = q.match(new RegExp('^([\\d.,]+)\\s*([' + SYMBOL_CHARS + '])$'))
    if (match) {
        const from = CURRENCY_ALIASES[match[2]]
        const amount = parseFloat(match[1].replace(',', '.'))
        if (from && amount > 0) return { amount, from, to: null }
    }

    match = q.match(new RegExp('^([' + SYMBOL_CHARS + '])\\s*([\\d.,]+)\\s*(?:kaç|to|=|->|→)\\s*(' + TARGET_PATTERN + ')$', 'i'))
    if (match) {
        const from = CURRENCY_ALIASES[match[1]]
        const amount = parseFloat(match[2].replace(',', '.'))
        const to = CURRENCY_ALIASES[match[3].toLowerCase()]
        if (from && to && amount > 0) return { amount, from, to }
    }

    match = q.match(new RegExp('^([\\d.,]+)\\s*([' + SYMBOL_CHARS + '])\\s*(?:kaç|to|=|->|→)\\s*(' + TARGET_PATTERN + ')$', 'i'))
    if (match) {
        const from = CURRENCY_ALIASES[match[2]]
        const amount = parseFloat(match[1].replace(',', '.'))
        const to = CURRENCY_ALIASES[match[3].toLowerCase()]
        if (from && to && amount > 0) return { amount, from, to }
    }

    match = q.match(new RegExp('^([\\d.,]+)\\s*(' + CURRENCY_WORDS + ')(?:\\s+(?:kaç|to|=|->|→)\\s+(' + TARGET_PATTERN + '))?$', 'i'))
    if (match) {
        const amount = parseFloat(match[1].replace(',', '.'))
        const from = CURRENCY_ALIASES[match[2].toLowerCase()]
        const to = match[3] ? CURRENCY_ALIASES[match[3].toLowerCase()] : null
        if (from && amount > 0) return { amount, from, to }
    }

    return null
}

function parseCurrencyMathExpression(query) {
    if (!query || query.trim().length < 3) return null
    const q = query.trim()

    if (!/[+\-*/]/.test(q)) return null

    const symbolPattern = '[' + SYMBOL_CHARS + ']'
    const wordPattern = CURRENCY_WORDS
    const currencyPattern = '(?:' + symbolPattern + '|' + wordPattern + ')'

    if (!new RegExp(currencyPattern, 'i').test(q)) return null

    let work = q.toLowerCase()
    let target = null

    const targetMatch = work.match(new RegExp('\\s+(?:kaç|to|=|->|→)\\s+(' + currencyPattern + ')\\s*$', 'i'))
    if (targetMatch) {
        target = CURRENCY_ALIASES[targetMatch[1].toLowerCase()] || null
        work = work.slice(0, targetMatch.index).trim()
    }

    const tokens = []
    const currencies = new Set()
    let rest = work.replace(/,(\d)/g, '.$1')

    const tokenRegex = new RegExp(
        '\\s*(?:' +
            '(\\d+(?:\\.\\d+)?)\\s*(' + symbolPattern + ')' +        // 43$
            '|(' + symbolPattern + ')\\s*(\\d+(?:\\.\\d+)?)' +       // $43
            '|(\\d+(?:\\.\\d+)?)\\s*(' + wordPattern + ')' +         // 43 dolar
            '|%(\\d+(?:\\.\\d+)?)' +                                 // %18
            '|(\\d+(?:\\.\\d+)?)%' +                                 // 18%
            '|(\\d+(?:\\.\\d+)?)' +                                  // 1.2
            '|([+\\-*/])' +                                          // operator
        ')\\s*', 'iy'
    )

    let match
    let lastIndex = 0
    while ((match = tokenRegex.exec(rest)) !== null) {
        if (match.index !== lastIndex) break
        lastIndex = tokenRegex.lastIndex

        if (match[1] && match[2]) {
            const cur = CURRENCY_ALIASES[match[2]]
            if (cur) { tokens.push({ type: 'amount', value: parseFloat(match[1]), currency: cur }); currencies.add(cur) }
        } else if (match[3] && match[4]) {
            const cur = CURRENCY_ALIASES[match[3]]
            if (cur) { tokens.push({ type: 'amount', value: parseFloat(match[4]), currency: cur }); currencies.add(cur) }
        } else if (match[5] && match[6]) {
            const cur = CURRENCY_ALIASES[match[6].toLowerCase()]
            if (cur) { tokens.push({ type: 'amount', value: parseFloat(match[5]), currency: cur }); currencies.add(cur) }
        } else if (match[7]) {
            tokens.push({ type: 'percent', value: parseFloat(match[7]) })
        } else if (match[8]) {
            tokens.push({ type: 'percent', value: parseFloat(match[8]) })
        } else if (match[9]) {
            tokens.push({ type: 'number', value: parseFloat(match[9]) })
        } else if (match[10]) {
            tokens.push({ type: 'op', op: match[10] })
        }
    }

    if (lastIndex < rest.length) {
        const trailing = rest.slice(lastIndex).trim()
        if (trailing) {
            const trailingCur = CURRENCY_ALIASES[trailing.toLowerCase()]
            if (trailingCur) {
                target = trailingCur
            } else {
                return null
            }
        }
    }

    if (currencies.size === 0) return null

    const hasOp = tokens.some(t => t.type === 'op')
    if (!hasOp) return null

    const currArr = [...currencies]
    const expression = q

    return { tokens, target, currencies: currArr, expression }
}

const SEARCH_ON_WIDGET = false
const COMPACT_HEIGHT = 94.8

export default {
    name: 'SpotlightPanel',
    components: { PanelFrame, BaseModal, SpotlightGroup, MathCalculator, ExchangeCalculator, CurrencyMathCalculator, UnitConverter, WeatherWidget },
    mixins: [
        createPanelMixin({
            panelId: 'spotlight',
            shortcut: { code: 'KeyS' },
            minWidth: 460,
            maxWidth: 700,
            minHeight: COMPACT_HEIGHT,
            defaults: { width: 640, height: 520, floatingMode: false, transparent: true },
            hasInnerLayout: false,
            showSettingsInCompact: true,
            settingsItems: ['alwaysOnTop', 'openOnLoad', 'divider', 'transparent', 'divider', 'resetWindowSize'],
            dividers: [],
            onOpen(vm) {
                vm.$nextTick(() => vm.$refs.searchInput?.focus())
            },
            onClose(vm) {
                vm.focusedIndex = -1
            },
            onEscape(vm) {
                if (vm.showGuide) {
                    vm.showGuide = false
                    return true
                }
                if (vm.searchQuery) {
                    vm.searchQuery = ''
                    vm.results = null
                    return true
                }
                return false
            },
            extraStorageKeys(vm) {
                return { collapsedGroups: vm.collapsedGroups }
            },
        })
    ],
    data() {
        const saved = panelManager.loadLayout('spotlight') || {}
        return {
            searchQuery: '',
            results: null,
            loading: false,
            collapsedGroups: saved.collapsedGroups || {},
            focusedIndex: -1,
            searchTimer: null,
            showGuide: false,
        }
    },
    computed: {
        panelStyle() {
            const ww = this.windowWidth
            const wh = this.windowHeight
            if (this.isCompact) {
                return { width: ww + 'px', height: wh + 'px', left: '0px', top: '0px' }
            }
            let w = this.panelWidth
            let h = this.panelHeight
            if (!this.floatingMode) {
                w = Math.min(w, Math.floor(ww * 0.95))
                h = Math.min(h, Math.floor(wh * 0.9))
            }
            const x = this.floatingMode
                ? Math.round(this.panelX ?? 100)
                : Math.round((ww - w) / 2)
            const style = { width: w + 'px', left: x + 'px' }
            if (this.isResizing) {
                style.height = h + 'px'
                style.top = this.floatingMode
                    ? Math.round(this.panelY ?? 50) + 'px'
                    : Math.round((wh - h) / 2) + 'px'
            } else {
                style.minHeight = COMPACT_HEIGHT + 'px'
                style.maxHeight = h + 'px'
                if (this.floatingMode) {
                    style.top = Math.round(this.panelY ?? 50) + 'px'
                } else {
                    style.top = '50%'
                    style.transform = 'translateY(-50%)'
                }
            }
            return style
        },
        isMac() {
            return /Mac|iPod|iPhone|iPad/.test(navigator.platform)
        },
        mathResult() {
            return parseMathExpression(this.searchQuery)
        },
        exchangeQuery() {
            return parseExchangeQuery(this.searchQuery)
        },
        currencyMathQuery() {
            if (this.mathResult) return null
            return parseCurrencyMathExpression(this.searchQuery)
        },
        unitQuery() {
            return parseUnitQuery(this.searchQuery)
        },
        hasWidget() {
            return this.mathResult !== null || this.currencyMathQuery !== null || this.exchangeQuery !== null || this.unitQuery !== null
        },
        isWeatherShowAll() {
            const q = (this.searchQuery || '').trim().toLocaleLowerCase('tr')
            return q.startsWith('h*:') || q.startsWith('hava*:')
        },
        weatherSearchTerm() {
            const q = (this.searchQuery || '').trim()
            const lower = q.toLocaleLowerCase('tr')
            if (lower.startsWith('hava*:')) return q.slice(6).trim()
            if (lower.startsWith('hava:')) return q.slice(5).trim()
            if (lower.startsWith('h*:')) return q.slice(3).trim()
            if (lower.startsWith('h:')) return q.slice(2).trim()
            return q
        },
        weatherItems() {
            if (!this.results) return []
            const group = this.results.find(g => g.key === 'MODULE_WEATHER')
            if (!group || !group.items.length) return []

            if (this.isWeatherShowAll) return group.items

            if (group.items.length <= 1) return group.items

            const term = this.weatherSearchTerm.toLocaleLowerCase('tr')
            if (term) {
                const match = group.items.find(i => (i.title || '').toLocaleLowerCase('tr').startsWith(term))
                if (match) return [match]
            }

            return [group.items[0]]
        },
        filteredResults() {
            if (!this.results) return null
            return this.results.filter(g => g.key !== 'MODULE_WEATHER')
        },
        quickActions() {
            const q = (this.searchQuery || '').trim().toLowerCase()
            if (!q) return []
            const prefix = this.$t('common.spotlight.quickActions.prefix').toLowerCase()
            const codes = this.$handler.getModuleCodes()
            const available = this.$handler.getQuickActions().filter(a => !a.moduleCode || codes.includes(a.moduleCode))
            if (q === prefix) return available
            if (q.startsWith(prefix + ' ')) {
                const term = q.slice(prefix.length + 1).trim()
                if (!term) return available
                return available.filter(a => {
                    const keywords = this.$t('common.spotlight.quickActions.' + a.keywordsKey).split(',').map(k => k.trim().toLowerCase())
                    return keywords.some(k => k.startsWith(term) || term.startsWith(k))
                })
            }
            if (q.length < 2) return []
            return available.filter(a => {
                const keywords = this.$t('common.spotlight.quickActions.' + a.keywordsKey).split(',').map(k => k.trim().toLowerCase())
                return keywords.some(k => k.startsWith(q) || q.startsWith(k))
            })
        },
        hasQuickActions() {
            return this.quickActions.length > 0
        },
        isQuickActionPrefixed() {
            const q = (this.searchQuery || '').trim().toLowerCase()
            const prefix = this.$t('common.spotlight.quickActions.prefix').toLowerCase()
            return q === prefix || q.startsWith(prefix + ' ')
        },
        hasResults() {
            return this.filteredResults !== null && this.filteredResults.length > 0
        },
        hasAnyContent() {
            return this.hasWidget || this.hasQuickActions || this.hasResults || this.weatherItems.length > 0 || this.loading || this.results !== null
        },
        flatItems() {
            if (!this.filteredResults) return []
            const items = []
            for (const group of this.filteredResults) {
                if (this.collapsedGroups[group.key]) continue
                for (const item of group.items) {
                    items.push({ groupKey: group.key, item })
                }
            }
            return items
        },
        totalNavigableCount() {
            return this.quickActions.length + this.flatItems.length
        },
        focusedQuickActionIdx() {
            if (this.focusedIndex < 0 || this.focusedIndex >= this.quickActions.length) return -1
            return this.focusedIndex
        },
        focusedGroupKey() {
            const resultIdx = this.focusedIndex - this.quickActions.length
            const fi = this.flatItems[resultIdx]
            return fi ? fi.groupKey : null
        },
        focusedIndexInGroup() {
            const resultIdx = this.focusedIndex - this.quickActions.length
            if (resultIdx < 0 || !this.flatItems[resultIdx]) return -1
            const fi = this.flatItems[resultIdx]
            const group = this.filteredResults?.find(g => g.key === fi.groupKey)
            if (!group) return -1
            return group.items.indexOf(fi.item)
        }
    },
    watch: {
        hasAnyContent(val) {
            if (val && this.panelHeight < 520) {
                this.panelHeight = 520
            }
        },
        searchQuery(val) {
            this.focusedIndex = -1
            clearTimeout(this.searchTimer)
            const trimmed = (val || '').trim()
            const prefix = this.$t('common.spotlight.quickActions.prefix').toLowerCase()
            const delay = /^[\d$€₺£₽¥₼]/.test(trimmed) || trimmed.toLowerCase().startsWith(prefix) ? 800 : 300
            this.searchTimer = setTimeout(() => this.doSearch(), delay)
        },
    },
    methods: {
        clearSearch() {
            this.searchQuery = ''
            this.results = null
            this.$refs.searchInput?.focus()
        },
        async doSearch() {
            const q = this.searchQuery.trim()
            if (!q) {
                this.results = null
                this.loading = false
                return
            }
            if (q.length < 2) {
                this.loading = false
                return
            }

            if (!SEARCH_ON_WIDGET && (this.hasWidget || (this.hasQuickActions && this.isQuickActionPrefixed))) {
                this.results = []
                return
            }

            let searchQ = q
            if (/^hava\*:/i.test(searchQ)) searchQ = 'hava:' + searchQ.slice(6)
            else if (/^h\*:/i.test(searchQ)) searchQ = 'h:' + searchQ.slice(3)

            this.loading = true
            try {
                const params = { q: searchQ, ...this.$handler.getContextParams() }
                const res = await this.axios.get(this.endpoints.spotlight_search, { params })
                this.results = res.data?.data?.results || []
            } catch (e) {
                this.results = []
            } finally {
                this.loading = false
            }
        },
        toggleGroup(key) {
            this.collapsedGroups = { ...this.collapsedGroups, [key]: !this.collapsedGroups[key] }
            this.panelSaveLayoutDebounced()
        },
        focusNext() {
            if (!this.totalNavigableCount) return
            this.focusedIndex = Math.min(this.focusedIndex + 1, this.totalNavigableCount - 1)
            this.scrollToFocused()
        },
        focusPrev() {
            if (!this.totalNavigableCount) return
            this.focusedIndex = Math.max(this.focusedIndex - 1, 0)
            this.scrollToFocused()
        },
        scrollToFocused() {
            this.$nextTick(() => {
                const container = this.$refs.resultsContainer
                if (!container) return
                const focused = container.querySelector('.spotlight-quick-action--focused, .spotlight-result-item--focused')
                if (focused) {
                    focused.scrollIntoView({ block: 'nearest', behavior: 'smooth' })
                }
            })
        },
        activateResult() {
            if (this.isCompact) this.$refs.searchInput?.blur()
            if (this.focusedIndex < 0 || this.focusedIndex >= this.totalNavigableCount) return
            if (this.focusedIndex < this.quickActions.length) {
                this.executeQuickAction(this.quickActions[this.focusedIndex])
                return
            }
            const fi = this.flatItems[this.focusedIndex - this.quickActions.length]
            if (!fi) return
            this.onResultSelect({ group: fi.groupKey, item: fi.item })
        },
        getQuickActionLabel(qa) {
            const map = {
                openNote: this.$t('common.spotlight.quickActions.newNote'),
                openAgenda: this.$t('common.spotlight.quickActions.newEvent'),
                newVWO: this.$t('common.spotlight.quickActions.newVWO'),
                newSWO: this.$t('common.spotlight.quickActions.newSWO'),
                newCustomer: this.$t('common.spotlight.quickActions.newCustomer'),
                newOfficer: this.$t('common.spotlight.quickActions.newOfficer'),
            }
            return map[qa.action] || ''
        },
        getQuickActionHint(qa) {
            const map = {
                openNote: this.$t('common.spotlight.quickActions.hintNote'),
                openAgenda: this.$t('common.spotlight.quickActions.hintAgenda'),
                newVWO: this.$t('common.spotlight.quickActions.hintVWO'),
                newSWO: this.$t('common.spotlight.quickActions.hintSWO'),
                newCustomer: this.$t('common.spotlight.quickActions.hintCustomer'),
                newOfficer: this.$t('common.spotlight.quickActions.hintOfficer'),
            }
            return map[qa.action] || ''
        },
        executeQuickAction(qa) {
            switch (qa.action) {
                case 'openNote':
                    panelManager.openPanel('noteApp', { newNote: true })
                    break
                case 'openAgenda':
                    panelManager.openPanel('agendaPanel', { newEvent: true, presetDate: this.$moment().format('YYYY-MM-DD HH:mm') })
                    break
                case 'newVWO':
                    this.$router.push('/module/vehicle-work-order/save')
                    break
                case 'newSWO':
                    this.$router.push('/module/service-work-order/save')
                    break
                case 'newCustomer':
                    this.$router.push('/customer/save')
                    break
                case 'newOfficer':
                    break
            }
            this.close()
        },
        onResultSelect({ group, item }) {
            const navMap = this.$handler.getNavigationMap()
            const handler = navMap[group]
            if (!handler) return
            handler(item, this.$router)
            this.close()
        },
    },
    beforeUnmount() {
        clearTimeout(this.searchTimer)
    }
}
</script>

<style>
.spotlight-app {
    --spotlight-search-bg: var(--panel-bg);
    --spotlight-result-hover: rgba(var(--panel-accent-rgb), 0.06);
    --spotlight-result-focused: rgba(var(--panel-accent-rgb), 0.10);
    --spotlight-group-color: var(--panel-text-secondary);
    --spotlight-widget-bg: rgba(var(--panel-accent-rgb), 0.04);
    --spotlight-icon-customer: #007AFF;
    --spotlight-icon-vehicle: #34C759;
    --spotlight-icon-officer: #AF52DE;
    --spotlight-icon-stock: #FF9500;
    --spotlight-icon-user: #5856D6;
    --spotlight-icon-company: #00C7BE;
    --spotlight-icon-workorder: #FF3B30;
    --spotlight-icon-note: #FFCC00;
    --spotlight-icon-agenda: #32ADE6;
    --spotlight-icon-weather: #FF9500;
    --spotlight-icon-file: #8E8E93;
    --spotlight-icon-filefolder: #3B82F6;
    --spotlight-icon-music: #8B5CF6;
}

html.dark .spotlight-app {
    --spotlight-icon-customer: #0A84FF;
    --spotlight-icon-vehicle: #30D158;
    --spotlight-icon-officer: #BF5AF2;
    --spotlight-icon-stock: #FF9F0A;
    --spotlight-icon-user: #5E5CE6;
    --spotlight-icon-company: #63E6E2;
    --spotlight-icon-workorder: #FF453A;
    --spotlight-icon-note: #FFD60A;
    --spotlight-icon-agenda: #40C8E0;
    --spotlight-icon-weather: #FF9F0A;
    --spotlight-icon-file: #98989D;
    --spotlight-icon-filefolder: #60A5FA;
    --spotlight-icon-music: #A78BFA;
}

.spotlight-titlebar-icon {
    display: flex;
    align-items: center;
    justify-content: center;
    color: var(--panel-text-primary);
}

.spotlight-titlebar-text {
    font-size: 13px;
    font-weight: 600;
    color: var(--panel-text-primary);
}

.spotlight-search-bar {
    display: flex;
    align-items: center;
    gap: 10px;
    padding: 12px 16px;
    border-bottom: 1px solid var(--panel-border);
    background: var(--spotlight-search-bg);
    flex-shrink: 0;
}
.spotlight-search-bar--no-border {
    border-bottom-color: transparent;
}

.spotlight-search-input {
    flex: 1;
    min-width: 0;
    border: none;
    outline: none;
    box-shadow: none;
    -webkit-appearance: none;
    background: transparent;
    font-size: 17px;
    font-weight: 400;
    color: var(--panel-text-primary);
    font-family: inherit;
    line-height: 1.4;
}

.spotlight-search-input::placeholder {
    color: var(--panel-text-secondary);
    opacity: 0.6;
}

.spotlight-search-clear {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 24px;
    height: 24px;
    border: none;
    background: none;
    border-radius: 50%;
    cursor: pointer;
    color: var(--panel-text-secondary);
    transition: background 0.15s;
    flex-shrink: 0;
}

.spotlight-search-clear:hover {
    background: var(--spotlight-result-hover);
    color: var(--panel-text-primary);
}

.spotlight-search-hint-icon {
    color: var(--panel-text-secondary);
    opacity: 0.5;
    flex-shrink: 0;
}

.spotlight-body {
    display: flex;
    flex-direction: column;
    flex: 1;
    width: 100%;
    min-height: 0;
    overflow: hidden;
}

.spotlight-results {
    flex: 1;
    min-height: 0;
    overflow-y: auto;
    overscroll-behavior: contain;
    padding-bottom: 8px;
}

.spotlight-widget-box {
    padding: 16px;
}

.spotlight-widget {
    display: flex;
    align-items: center;
    gap: 14px;
    border-radius: 10px;
    padding: 14px 16px;
    transition: background 0.15s;
    position: relative;
}

.spotlight-widget--clickable {
    cursor: pointer;
}

.spotlight-widget--clickable:hover {
    background: var(--spotlight-result-focused);
}

.spotlight-widget-icon {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 38px;
    height: 38px;
    border-radius: 10px;
    flex-shrink: 0;
    color: #fff;
}

.spotlight-widget-icon--math {
    background: linear-gradient(135deg, #5856D6, #AF52DE);
}

.spotlight-widget-icon--exchange {
    background: linear-gradient(135deg, #34C759, #30D158);
}

.spotlight-widget-icon--unit {
    background: linear-gradient(135deg, #007AFF, #32ADE6);
}

.spotlight-widget-icon--error {
    background: linear-gradient(135deg, #FF9500, #FF3B30);
}

.spotlight-widget-content {
    flex: 1;
    min-width: 0;
}

.spotlight-widget-result {
    font-size: 22px;
    font-weight: 600;
    color: var(--panel-text-primary);
    line-height: 1.3;
}

.spotlight-widget-result--loading {
    font-size: 18px;
    color: var(--panel-text-secondary);
}

.spotlight-widget-eq {
    color: #5856D6;
    font-weight: 700;
}

html.dark .spotlight-widget-eq {
    color: #7D7AFF;
}

.spotlight-widget-arrow {
    color: #34C759;
    font-weight: 400;
    font-size: 18px;
    vertical-align: middle;
}

html.dark .spotlight-widget-arrow {
    color: #30D158;
}

.spotlight-widget-expression {
    font-size: 12px;
    color: var(--panel-text-secondary);
    margin-top: 2px;
}

.spotlight-widget-error-text {
    font-size: 13px;
    font-weight: 500;
    color: #FF9500;
}

html.dark .spotlight-widget-error-text {
    color: #FF9F0A;
}

.spotlight-widget-copied {
    position: absolute;
    top: 8px;
    right: 12px;
    font-size: 11px;
    color: var(--panel-text-secondary);
    background: var(--panel-bg);
    border: 1px solid var(--panel-border);
    border-radius: 4px;
    padding: 2px 8px;
}

.spotlight-math-widget {
    background: rgba(88, 86, 214, 0.06);
}

.spotlight-exchange-widget {
    background: rgba(52, 199, 89, 0.06);
}

.spotlight-exchange-widget:has(.spotlight-widget-icon--error) {
    background: rgba(255, 149, 0, 0.06);
}

html.dark .spotlight-math-widget {
    background: rgba(125, 122, 255, 0.08);
}

html.dark .spotlight-exchange-widget {
    background: rgba(48, 209, 88, 0.08);
}

html.dark .spotlight-exchange-widget:has(.spotlight-widget-icon--error) {
    background: rgba(255, 159, 10, 0.08);
}

.spotlight-unit-widget {
    background: var(--spotlight-widget-bg);
}

.spotlight-widget-divider {
    height: 1px;
    background: var(--panel-border);
    margin: 0 16px;
}

.spotlight-group {
    margin-bottom: 2px;
}

.spotlight-group-header {
    display: flex;
    align-items: center;
    gap: 6px;
    padding: 10px 16px 4px;
    cursor: pointer;
    user-select: none;
}

.spotlight-group-header:hover .spotlight-group-label {
    color: var(--panel-text-primary);
}

.spotlight-group-chevron {
    color: var(--panel-text-secondary);
    transition: transform 0.2s;
    flex-shrink: 0;
}

.spotlight-group-chevron--collapsed {
    transform: rotate(-90deg);
}

.spotlight-group-label {
    font-size: 11px;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.5px;
    color: var(--spotlight-group-color);
    transition: color 0.15s;
}

.spotlight-group-count {
    font-size: 10px;
    color: var(--panel-text-secondary);
    background: var(--spotlight-result-hover);
    border-radius: 8px;
    padding: 1px 6px;
    min-width: 18px;
    text-align: center;
}

.spotlight-group-items {
    padding: 2px 8px 4px;
}

.spotlight-result-item {
    display: flex;
    align-items: center;
    gap: 10px;
    padding: 8px 12px;
    border-radius: 8px;
    cursor: pointer;
    transition: background 0.12s;
}

.spotlight-result-item:hover {
    background: var(--spotlight-result-hover);
}

.spotlight-result-item--focused {
    background: var(--spotlight-result-focused);
}

.spotlight-result-icon {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 34px;
    height: 34px;
    border-radius: 8px;
    flex-shrink: 0;
    color: #fff;
}

.spotlight-result-icon--customer { background: var(--spotlight-icon-customer); }
.spotlight-result-icon--vehicle { background: var(--spotlight-icon-vehicle); }
.spotlight-result-icon--officer { background: var(--spotlight-icon-officer); }
.spotlight-result-icon--stock { background: var(--spotlight-icon-stock); }
.spotlight-result-icon--user { background: var(--spotlight-icon-user); }
.spotlight-result-icon--company { background: var(--spotlight-icon-company); }
.spotlight-result-icon--workorder { background: var(--spotlight-icon-workorder); }
.spotlight-result-icon--note { background: var(--spotlight-icon-note); }
.spotlight-result-icon--agenda { background: var(--spotlight-icon-agenda); }
.spotlight-result-icon--weather { background: var(--spotlight-icon-weather); }
.spotlight-result-icon--file { background: var(--spotlight-icon-file); }
.spotlight-result-icon--filefolder { background: var(--spotlight-icon-filefolder); }
.spotlight-result-icon--music { background: var(--spotlight-icon-music); }
.spotlight-result-icon--music-thumb { overflow: hidden; padding: 0; }
.spotlight-result-icon--music-thumb img { width: 100%; height: 100%; object-fit: cover; }
.spotlight-result-icon--file-preview {
    overflow: hidden;
    padding: 0;
}
.spotlight-result-icon--file-preview img {
    width: 100%;
    height: 100%;
    object-fit: cover;
}

.spotlight-file-subtitle {
    display: flex;
    align-items: center;
    gap: 5px;
}
.spotlight-file-subtitle .spotlight-inline-sep {
    margin: 0;
}
.spotlight-file-ext {
    font-weight: 600;
}

.spotlight-file-meta-cycle {
    min-width: 0;
    max-width: 110px;
}
.spotlight-file-relation {
    display: flex;
    align-items: center;
    gap: 4px;
    font-size: 10px;
    font-weight: 500;
    white-space: nowrap;
    color: var(--panel-text-secondary);
    max-width: 100%;
}
.spotlight-file-relation svg {
    flex-shrink: 0;
}
.spotlight-file-relation-label {
    overflow: hidden;
    text-overflow: ellipsis;
    min-width: 0;
}
.spotlight-file-relation--note { color: #F59E0B; }
.spotlight-file-relation--workorder { color: #0EA5E9; }
.spotlight-file-relation--folder { color: #3B82F6; }
html.dark .spotlight-file-relation--note { color: #FBBF24; }
html.dark .spotlight-file-relation--workorder { color: #38BDF8; }
html.dark .spotlight-file-relation--folder { color: #60A5FA; }
.spotlight-rel-fade-enter-active,
.spotlight-rel-fade-leave-active {
    transition: opacity 0.3s, transform 0.3s;
}
.spotlight-rel-fade-enter-from {
    opacity: 0;
    transform: translateY(6px);
}
.spotlight-rel-fade-leave-to {
    opacity: 0;
    transform: translateY(-6px);
}

.spotlight-result-text {
    flex: 1;
    min-width: 0;
}

.spotlight-result-title {
    font-size: 13px;
    font-weight: 500;
    color: var(--panel-text-primary);
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
}

.spotlight-result-title:has(.spotlight-result-dot) {
    display: flex;
    align-items: center;
    gap: 6px;
}

.spotlight-result-title-text {
    overflow: hidden;
    text-overflow: ellipsis;
    min-width: 0;
}

.spotlight-result-subtitle {
    font-size: 11px;
    color: var(--panel-text-secondary);
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    margin-top: 1px;
}

.spotlight-result-subtitle:has(.spotlight-status-badge--inline) {
    display: flex;
    align-items: center;
    min-width: 0;
}

.spotlight-result-subtitle-text {
    overflow: hidden;
    text-overflow: ellipsis;
    min-width: 0;
}

.spotlight-result-badge {
    font-size: 11px;
    color: var(--panel-text-secondary);
    background: var(--spotlight-result-hover);
    border-radius: 6px;
    padding: 2px 8px;
    white-space: nowrap;
    flex-shrink: 0;
}

.spotlight-stock-qty {
    font-size: 10px;
    font-weight: 600;
    color: var(--spotlight-icon-stock);
    white-space: nowrap;
}

.spotlight-status-badge {
    font-size: 10px;
    font-weight: 600;
    border-radius: 4px;
    padding: 1px 6px;
    white-space: nowrap;
    flex-shrink: 0;
    line-height: 1.4;
}

.spotlight-status-badge--small {
    font-size: 9px;
    padding: 0 5px;
}

.spotlight-status-badge--inline {
    margin-right: 4px;
    position: relative;
    top: -0.5px;
}

.spotlight-inline-sep {
    margin-right: 5px;
    font-size: 13px;
    font-weight: 700;
    color: #b0b0b5;
}

html.dark .spotlight-inline-sep {
    color: #5a5a65;
}

.spotlight-status--open { color: var(--panel-accent-text); background: rgba(var(--panel-accent-rgb), 0.1); }
.spotlight-status--processing { color: #FF9500; background: rgba(255, 149, 0, 0.1); }
.spotlight-status--completed, .spotlight-status--paid { color: #34C759; background: rgba(52, 199, 89, 0.1); }
.spotlight-status--waiting { color: #E5A100; background: rgba(229, 161, 0, 0.1); }
.spotlight-status--cancelled { color: #FF3B30; background: rgba(255, 59, 48, 0.1); }
.spotlight-status--refunded { color: #AF52DE; background: rgba(175, 82, 222, 0.1); }
.spotlight-status--pending { color: var(--panel-text-secondary); background: var(--spotlight-result-hover); }
.spotlight-status--partial { color: #FF9500; background: rgba(255, 149, 0, 0.1); }

html.dark .spotlight-status--processing { color: #FF9F0A; background: rgba(255, 159, 10, 0.15); }
html.dark .spotlight-status--completed, html.dark .spotlight-status--paid { color: #30D158; background: rgba(48, 209, 88, 0.15); }
html.dark .spotlight-status--waiting { color: #FFD60A; background: rgba(255, 214, 10, 0.15); }
html.dark .spotlight-status--cancelled { color: #FF453A; background: rgba(255, 69, 58, 0.15); }
html.dark .spotlight-status--refunded { color: #BF5AF2; background: rgba(191, 90, 242, 0.15); }
html.dark .spotlight-status--partial { color: #FF9F0A; background: rgba(255, 159, 10, 0.15); }

.spotlight-result-meta {
    display: flex;
    flex-direction: column;
    align-items: flex-end;
    gap: 3px;
    flex-shrink: 0;
}

.spotlight-result-meta-muted {
    font-size: 10px;
    color: var(--panel-text-secondary);
    opacity: 0.6;
    white-space: nowrap;
}

.spotlight-payment-total {
    font-size: 11px;
    font-weight: 700;
    color: var(--panel-text-primary);
    white-space: nowrap;
}

.spotlight-result-payment {
    display: flex;
    align-items: center;
    gap: 4px;
}

.spotlight-payment-amount {
    font-size: 10px;
    font-weight: 600;
    color: #FF9500;
    white-space: nowrap;
}

html.dark .spotlight-payment-amount {
    color: #FF9F0A;
}

.spotlight-payment-sep {
    color: var(--panel-text-secondary);
    opacity: 0.4;
    font-size: 10px;
}

.spotlight-shared-by {
    display: flex;
    align-items: center;
    gap: 3px;
    font-size: 10px;
    color: var(--panel-text-secondary);
    opacity: 0.7;
    white-space: nowrap;
}

.spotlight-result-dot {
    display: inline-block;
    width: 8px;
    height: 8px;
    border-radius: 50%;
    flex-shrink: 0;
}

.spotlight-empty {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    padding: 20px 24px;
    color: var(--panel-text-secondary);
    text-align: center;
}

.spotlight-empty-icon {
    opacity: 0.25;
    margin-bottom: 10px;
}

.spotlight-empty p {
    font-size: 13px;
    margin: 0;
}

.spotlight-loading {
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 40px 24px;
}

.spotlight-loading--inline {
    padding: 16px 24px;
}

.spotlight-loading-spinner {
    width: 26px;
    height: 26px;
    border: 3px solid var(--panel-border);
    border-top-color: var(--panel-text-secondary);
    border-radius: 50%;
    animation: spotlightSpin 0.7s linear infinite;
}

.spotlight-loading-spinner--small {
    width: 18px;
    height: 18px;
    border-width: 2px;
}

@keyframes spotlightSpin {
    to { transform: rotate(360deg); }
}

.spotlight-app.panel-app--compact .spotlight-search-bar {
    padding: 8px 12px;
}

.spotlight-app.panel-app--compact .spotlight-search-input {
    font-size: 15px;
}

.spotlight-app.panel-app--compact .spotlight-widget-result {
    font-size: 18px;
}

.spotlight-app.panel-app--compact .spotlight-widget-icon {
    width: 32px;
    height: 32px;
    border-radius: 8px;
}

.spotlight-app.panel-app--compact .spotlight-widget-icon svg {
    width: 15px;
    height: 15px;
}

.spotlight-guide {
    --sg-bg: #fff;
    --sg-text: #1d1d1f;
    --sg-text-secondary: #86868b;
    --sg-border: #e5e5ea;
    --sg-hover: rgba(var(--panel-accent-rgb), 0.06);
    background: var(--sg-bg);
    color: var(--sg-text);
    display: flex;
    flex-direction: column;
}

html.dark .spotlight-guide {
    --sg-bg: #15171C;
    --sg-text: #F5F5F5;
    --sg-text-secondary: #9A9CAE;
    --sg-border: #26272F;
    --sg-hover: rgba(var(--panel-accent-rgb), 0.1);
}

.spotlight-guide-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 18px 20px 14px;
    border-bottom: 1px solid var(--sg-border);
    flex-shrink: 0;
}

html.dark .spotlight-guide-header {
    border-color: rgba(255, 255, 255, 0.06);
}

.spotlight-guide-header h3 {
    margin: 0;
    font-size: 15px;
    font-weight: 700;
    color: var(--sg-text);
}

.spotlight-guide-close {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 28px;
    height: 28px;
    border: none;
    background: none;
    border-radius: 8px;
    cursor: pointer;
    color: var(--sg-text-secondary);
    transition: background 0.15s, color 0.15s;
}

.spotlight-guide-close:hover {
    background: var(--sg-hover);
    color: var(--sg-text);
}

.spotlight-guide-body {
    overflow-y: auto;
    overscroll-behavior: contain;
    padding: 12px 20px 20px;
}

.spotlight-guide-section {
    display: flex;
    gap: 14px;
    padding: 18px 0;
}

.spotlight-guide-section + .spotlight-guide-section {
    border-top: 1px solid var(--sg-border);
}

html.dark .spotlight-guide-section + .spotlight-guide-section {
    border-color: rgba(255, 255, 255, 0.06);
}

.spotlight-guide-section-icon {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 38px;
    height: 38px;
    border-radius: 10px;
    flex-shrink: 0;
    color: #fff;
}

.spotlight-guide-icon--search {
    background: linear-gradient(135deg, #007AFF, #5856D6);
}

.spotlight-guide-icon--prefix {
    background: linear-gradient(135deg, #FF9500, #FF3B30);
}

.spotlight-guide-icon--math {
    background: linear-gradient(135deg, #5856D6, #AF52DE);
}

.spotlight-guide-icon--currency {
    background: linear-gradient(135deg, #34C759, #30D158);
}

.spotlight-guide-icon--unit {
    background: linear-gradient(135deg, #007AFF, #32ADE6);
}

.spotlight-guide-icon--weather {
    background: linear-gradient(135deg, #FF9500, #FF6B00);
}

.spotlight-guide-icon--keyboard {
    background: linear-gradient(135deg, #8E8E93, #636366);
}

.spotlight-guide-section-content {
    flex: 1;
    min-width: 0;
}

.spotlight-guide-section-content h4 {
    margin: 0 0 5px;
    font-size: 13px;
    font-weight: 700;
    color: var(--sg-text);
}

.spotlight-guide-section-content p {
    margin: 0 0 10px;
    font-size: 12px;
    line-height: 1.5;
    color: var(--sg-text-secondary);
}

.sg-example-list {
    display: flex;
    flex-direction: column;
    border-left: 3px solid;
}

.sg-example-row {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 6px 10px;
    border-bottom: 1px solid rgba(0, 0, 0, 0.06);
}

.sg-example-row:last-child {
    border-bottom: none;
}

html.dark .sg-example-row {
    border-bottom-color: rgba(255, 255, 255, 0.06);
}

.sg-example-input {
    font-family: 'SF Mono', 'Menlo', 'Monaco', monospace;
    font-size: 12px;
    font-weight: 600;
}

.sg-example-result {
    font-size: 11px;
    opacity: 0.6;
}

.sg-example--blue {
    border-color: #007AFF;
    background: rgba(0, 122, 255, 0.05);
    color: var(--sg-text);
}

.sg-example--blue .sg-example-result {
    color: #007AFF;
}

.sg-example--purple {
    border-color: #5856D6;
    background: rgba(88, 86, 214, 0.05);
    color: var(--sg-text);
}

.sg-example--purple .sg-example-result {
    color: #5856D6;
}

.sg-example--green {
    border-color: #34C759;
    background: rgba(52, 199, 89, 0.05);
    color: var(--sg-text);
}

.sg-example--green .sg-example-result {
    color: #34C759;
}

html.dark .sg-example--blue {
    border-color: #0A84FF;
    background: rgba(10, 132, 255, 0.08);
}

html.dark .sg-example--blue .sg-example-result {
    color: #0A84FF;
}

html.dark .sg-example--purple {
    border-color: #7D7AFF;
    background: rgba(125, 122, 255, 0.08);
}

html.dark .sg-example--purple .sg-example-result {
    color: #7D7AFF;
}

html.dark .sg-example--green {
    border-color: #30D158;
    background: rgba(48, 209, 88, 0.08);
}

html.dark .sg-example--green .sg-example-result {
    color: #30D158;
}

.sg-example--cyan {
    border-color: #32ADE6;
    background: rgba(50, 173, 230, 0.05);
    color: var(--sg-text);
}

.sg-example--cyan .sg-example-result {
    color: #32ADE6;
}

html.dark .sg-example--cyan {
    border-color: #40C8E0;
    background: rgba(64, 200, 224, 0.08);
}

html.dark .sg-example--cyan .sg-example-result {
    color: #40C8E0;
}

.sg-example--orange {
    border-color: #FF9500;
    background: rgba(255, 149, 0, 0.05);
    color: var(--sg-text);
}

.sg-example--orange .sg-example-result {
    color: #FF9500;
}

html.dark .sg-example--orange {
    border-color: #FF9F0A;
    background: rgba(255, 159, 10, 0.08);
}

html.dark .sg-example--orange .sg-example-result {
    color: #FF9F0A;
}

.spotlight-guide-prefix-grid {
    display: grid;
    grid-template-columns: 1fr 1fr 1fr;
    gap: 6px;
    margin-bottom: 14px;
}

.sg-prefix-card {
    display: flex;
    align-items: center;
    gap: 6px;
    padding: 6px 10px;
    border-radius: 0;
    border-left: 3px solid;
}

.sg-prefix-code {
    font-family: 'SF Mono', 'Menlo', 'Monaco', monospace;
    font-size: 12px;
    font-weight: 700;
}

.sg-prefix-label {
    font-size: 11px;
    opacity: 0.75;
}

.sg-prefix--blue   { border-color: #007AFF; background: rgba(0, 122, 255, 0.06); color: #007AFF; }
.sg-prefix--green  { border-color: #34C759; background: rgba(52, 199, 89, 0.06); color: #34C759; }
.sg-prefix--orange { border-color: #FF9500; background: rgba(255, 149, 0, 0.06); color: #FF9500; }
.sg-prefix--red    { border-color: #FF3B30; background: rgba(255, 59, 48, 0.06); color: #FF3B30; }
.sg-prefix--yellow { border-color: #C79A00; background: rgba(199, 154, 0, 0.06); color: #C79A00; }
.sg-prefix--cyan   { border-color: #32ADE6; background: rgba(50, 173, 230, 0.06); color: #32ADE6; }

html.dark .sg-prefix--blue   { border-color: #0A84FF; background: rgba(10, 132, 255, 0.10); color: #0A84FF; }
html.dark .sg-prefix--green  { border-color: #30D158; background: rgba(48, 209, 88, 0.10); color: #30D158; }
html.dark .sg-prefix--orange { border-color: #FF9F0A; background: rgba(255, 159, 10, 0.10); color: #FF9F0A; }
html.dark .sg-prefix--red    { border-color: #FF453A; background: rgba(255, 69, 58, 0.10); color: #FF453A; }
html.dark .sg-prefix--yellow { border-color: #FFD60A; background: rgba(255, 214, 10, 0.10); color: #FFD60A; }
html.dark .sg-prefix--cyan   { border-color: #40C8E0; background: rgba(64, 200, 224, 0.10); color: #40C8E0; }

.sg-prefix-demo {
    display: flex;
    align-items: center;
    gap: 6px;
    padding: 7px 12px;
    border-radius: 0;
    background: var(--sg-hover);
    font-size: 12px;
    color: var(--sg-text-secondary);
}

.sg-prefix-demo svg {
    opacity: 0.4;
    flex-shrink: 0;
}

.sg-prefix-demo-prefix {
    font-family: 'SF Mono', 'Menlo', 'Monaco', monospace;
    font-weight: 700;
    color: var(--panel-accent-text);
}

.sg-prefix-demo-term {
    font-family: 'SF Mono', 'Menlo', 'Monaco', monospace;
    color: var(--sg-text);
}

.sg-prefix-demo-arrow {
    color: var(--sg-text-secondary);
    opacity: 0.4;
}

.sg-prefix-demo-desc {
    font-size: 11px;
    color: var(--sg-text-secondary);
    opacity: 0.7;
}

.spotlight-guide-shortcuts {
    display: flex;
    flex-direction: column;
    gap: 8px;
    margin-top: 8px;
}

.spotlight-guide-shortcuts > div {
    display: flex;
    align-items: center;
    gap: 5px;
    font-size: 12px;
    color: var(--sg-text-secondary);
}

.spotlight-guide-shortcuts > div > span {
    margin-left: auto;
    font-size: 11px;
    color: var(--sg-text-secondary);
    opacity: 0.7;
}

.spotlight-guide-shortcuts kbd {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    min-width: 24px;
    height: 22px;
    padding: 0 6px;
    font-family: inherit;
    font-size: 11px;
    font-weight: 600;
    color: var(--sg-text);
    background: var(--sg-hover);
    border: 1px solid var(--sg-border);
    border-radius: 5px;
    box-shadow: 0 1px 0 var(--sg-border);
}

html.midnight .spotlight-app {
    --spotlight-icon-customer: #0A84FF;
    --spotlight-icon-vehicle: #30D158;
    --spotlight-icon-officer: #BF5AF2;
    --spotlight-icon-stock: #FF9F0A;
    --spotlight-icon-user: #5E5CE6;
    --spotlight-icon-company: #63E6E2;
    --spotlight-icon-workorder: #FF453A;
    --spotlight-icon-note: #FFD60A;
    --spotlight-icon-agenda: #40C8E0;
    --spotlight-icon-weather: #FF9F0A;
    --spotlight-icon-file: #98989D;
    --spotlight-icon-filefolder: #60A5FA;
    --spotlight-icon-music: #A78BFA;
}

html.midnight .spotlight-guide {
    --sg-bg: #1A1714;
    --sg-text: #EAE6E1;
    --sg-text-secondary: #A9A39D;
    --sg-border: #2C2823;
    --sg-hover: rgba(200, 134, 60, 0.1);
}

html.midnight .spotlight-guide-header {
    border-color: rgba(255, 255, 255, 0.04);
}

html.midnight .spotlight-guide-section + .spotlight-guide-section {
    border-color: rgba(255, 255, 255, 0.04);
}

html.midnight .sg-example-row {
    border-bottom-color: rgba(255, 255, 255, 0.04);
}

html.cream .spotlight-app {
    --spotlight-result-hover: rgba(5, 150, 105, 0.06);
    --spotlight-result-focused: rgba(5, 150, 105, 0.1);
    --spotlight-widget-bg: rgba(5, 150, 105, 0.04);
}

html.cream .spotlight-guide {
    --sg-bg: #FEFCF8;
    --sg-text: #3D3832;
    --sg-text-secondary: #847C72;
    --sg-border: #E8E3DA;
    --sg-hover: rgba(5, 150, 105, 0.06);
}

.spotlight-quick-actions {
    padding: 8px 8px 4px;
}

.spotlight-quick-actions-header {
    font-size: 11px;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.5px;
    color: var(--spotlight-group-color);
    padding: 6px 12px 4px;
}

.spotlight-quick-action {
    display: flex;
    align-items: center;
    gap: 10px;
    padding: 10px 12px;
    border-radius: 8px;
    cursor: pointer;
    transition: background 0.12s;
}

.spotlight-quick-action:hover {
    background: var(--spotlight-result-hover);
}

.spotlight-quick-action--focused {
    background: var(--spotlight-result-focused);
}

.spotlight-quick-action-text {
    flex: 1;
    min-width: 0;
}

.spotlight-quick-action-label {
    font-size: 13px;
    font-weight: 500;
    color: var(--panel-text-primary);
    display: block;
}

.spotlight-quick-action-hint {
    font-size: 11px;
    color: var(--panel-text-secondary);
    display: block;
    margin-top: 1px;
}

.spotlight-quick-action-arrow {
    flex-shrink: 0;
    color: var(--panel-text-secondary);
    opacity: 0.5;
}

.panel-content--transparent .spotlight-search-bar {
    background: transparent;
    border-bottom: none;
}
.panel-content--transparent .spotlight-search-bar:not(.spotlight-search-bar--no-border) {
    border-bottom: 1px solid rgba(0, 0, 0, 0.08);
}
html.dark .panel-content--transparent .spotlight-search-bar:not(.spotlight-search-bar--no-border),
html.midnight .panel-content--transparent .spotlight-search-bar:not(.spotlight-search-bar--no-border) {
    border-bottom-color: transparent;
}
.panel-content--transparent .spotlight-body {
    background: transparent;
}

</style>
